/**
 * Onboarding milestone helpers.
 *
 * Milestones are a FIXED, ordered set of checkpoints, each anchored to a "Day N"
 * offset from the start date (start = Day 1, matching getJourneyDayNumber). The
 * day offset is the primitive — it's stable regardless of which weekday the hire
 * starts on, and makes "next milestone" + countdown trivial arithmetic. We label
 * some with a friendlier name ("End of Week 1", "6 Months"); the number is only
 * used for ordering and the countdown, never displayed raw.
 *
 * We deliberately don't derive these from business-day math on calendar-month
 * boundaries — that produced off-by-a-few and negative numbers ("Day 31", "Day -2").
 * The day offset stays weekend-agnostic; when we surface a real calendar date we
 * snap it to the nearest weekday (see milestoneObservedDate), and the countdown
 * derives from that snapped date so it stays consistent with the label.
 */

import { addDays, differenceInCalendarDays, format, getDay } from "date-fns";
import { parseDateOnly } from "helpers/time";

export interface Milestone {
  day: number;
  label: string;
}

// Day-offset milestones. Not the stage-key stages in features/Stage/helpers.ts
// (getStageStatus) — different axes, keep separate.
export const MILESTONES: Milestone[] = [
  { day: 1, label: "Day 1" },
  { day: 7, label: "End of Week 1" },
  { day: 14, label: "End of Week 2" },
  { day: 30, label: "Day 30" },
  { day: 60, label: "Day 60" },
  { day: 90, label: "Day 90" },
  { day: 180, label: "6 Months" },
  { day: 365, label: "1 Year" },
];

/** The next milestone strictly after the current journey day; null once past the last. */
export function nextMilestone(journeyDay: number | null | undefined): Milestone | null {
  if (journeyDay == null) return null;
  return MILESTONES.find((milestone) => milestone.day > journeyDay) ?? null;
}

/** Nearest weekday: Saturday snaps back to Friday, Sunday forward to Monday. */
function snapToWeekday(date: Date): Date {
  const dayOfWeek = getDay(date);
  if (dayOfWeek === 6) return addDays(date, -1);
  if (dayOfWeek === 0) return addDays(date, 1);
  return date;
}

/**
 * The calendar date a milestone is observed on, snapped off weekends (start = Day 1).
 * Day N is a naive calendar offset from the start date — the viewer's timezone must
 * not shift which weekday it lands on, so we parse the date without one.
 */
export function milestoneObservedDate(
  startDate: Date | string | undefined,
  milestone: Milestone | null | undefined,
): Date | null {
  if (!startDate || !milestone) return null;
  return snapToWeekday(addDays(parseDateOnly(startDate), milestone.day - 1));
}

/** Whole days from today until a milestone's observed (weekday-snapped) date. Never negative. */
export function daysUntilMilestone(
  startDate: Date | string | undefined,
  journeyDay: number | null | undefined,
  milestone: Milestone | null | undefined,
): number | null {
  if (journeyDay == null || milestone == null) return null;
  const observed = milestoneObservedDate(startDate, milestone);
  if (!observed || !startDate) return Math.max(0, milestone.day - journeyDay);
  const today = addDays(parseDateOnly(startDate), journeyDay - 1);
  return Math.max(0, differenceInCalendarDays(observed, today));
}

/** Human countdown for a milestone: "Today" / "Tomorrow" / "In N days". */
export function milestoneCountdownLabel(daysUntil: number | null | undefined): string | null {
  if (daysUntil == null) return null;
  if (daysUntil === 0) return "Today";
  if (daysUntil === 1) return "Tomorrow";
  return `In ${daysUntil} days`;
}

/** The milestone's observed calendar date, e.g. "Friday, August 28" (weekend-snapped). */
export function milestoneDateLabel(
  startDate: Date | string | undefined,
  milestone: Milestone | null | undefined,
): string | null {
  const observed = milestoneObservedDate(startDate, milestone);
  return observed ? format(observed, "EEEE, MMMM d") : null;
}
